import React from "react";
import "./ScrollSentinel.css";

interface Props {
  /** Fired whenever the sentinel comes within `prefetchPx` of the scroll
   *  container's bottom edge. */
  onVisible: () => void;
  /** True while a page is loading — drives the "Loading more…" hint and the
   *  post-load re-check. */
  loading: boolean;
  /** CSS selector of the scrolling ancestor. Used as the IntersectionObserver
   *  root and for the post-load intersection re-check. */
  scrollSelector: string;
  /** Distance from the container's bottom at which to start prefetching the
   *  next page. Larger = the next batch loads earlier, so it's ready before
   *  the user reaches it. */
  prefetchPx?: number;
}

/**
 * IntersectionObserver-based infinite-scroll trigger. Place it at the end of a
 * scrollable list; it calls `onVisible` to prefetch the next page.
 *
 * The observer roots on the scroll container (not the viewport), so a tall
 * `prefetchPx` margin fires the fetch while the sentinel is still well below
 * the fold — the next batch loads ahead of arrival instead of on it. The
 * sentinel is pinned to the container's left edge (sticky), so it stays within
 * the container's horizontal viewport at any horizontal scroll position and
 * keeps firing even when scrolled fully across a wide table.
 *
 * IntersectionObserver only fires on threshold crossings, not while an element
 * stays in view. When the rendered list is short the sentinel can be
 * intersecting from first render and stay intersecting after each appended
 * page — so the observer never re-fires and pagination stalls. To cover that,
 * we also re-check via getBoundingClientRect after every `loading` cycle and
 * fire again if the sentinel is still within the prefetch band.
 */
const ScrollSentinel: React.FC<Props> = ({
  onVisible,
  loading,
  scrollSelector,
  prefetchPx = 200,
}) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const containerRef = React.useRef<HTMLElement | null>(null);
  const cbRef = React.useRef(onVisible);
  cbRef.current = onVisible;

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const container = el.closest(scrollSelector) as HTMLElement | null;
    containerRef.current = container;
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) cbRef.current();
        }
      },
      { root: container, rootMargin: `${prefetchPx}px` },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [scrollSelector, prefetchPx]);

  React.useEffect(() => {
    if (loading) return;
    const id = requestAnimationFrame(() => {
      const el = ref.current;
      const container = containerRef.current;
      if (!el || !container) return;
      const elRect = el.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const inView = elRect.top < containerRect.bottom + prefetchPx;
      if (inView) cbRef.current();
    });
    return () => cancelAnimationFrame(id);
  }, [loading, prefetchPx]);

  return (
    <div ref={ref} className="scroll-sentinel">
      {loading && <span className="scroll-sentinel-loading">Loading more…</span>}
    </div>
  );
};

export default ScrollSentinel;
